import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { registerUser } from "../api/users";
import {
    Box, TextField, Button, Typography, Alert, Divider
} from "@mui/material";
import { Google } from "@mui/icons-material";


const RegisterPage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        confirm_password: "",
    });
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = t("auth.emailRequired");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t("auth.invalidEmail");
        }
        if (!formData.password || formData.password.length < 6) {
            newErrors.password = t("auth.passwordMinLength");
        }
        if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = t("auth.passwordsDoNotMatch");
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError(null);
        setLoading(true);

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        try {
            await registerUser(formData);
            navigate("/verify-email");
        } catch (err) {
            if(err?.email){
                setErrors({ email: t("auth.emailExists") });
            } else {
            setServerError(err.message || t("auth.registerError"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleRegister = () => {
        window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/users/google/login/`;
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", mt: 8 }}>
            <Box sx={{
                maxWidth: 400,
                width: "100%",
                p: 4,
                boxShadow: 3,
                borderRadius: 2,
                textAlign: "center",
                backgroundColor: "white",
            }}>
                <Typography variant="h4" fontWeight="bold" mb={3}>
                    {t("auth.registration")}
                </Typography>

                {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}

                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Email"
                        name="email"
                        type="email"
                        fullWidth
                        required
                        variant="outlined"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label={t("auth.password")}
                        name="password"
                        type="password"
                        fullWidth
                        required
                        variant="outlined"
                        value={formData.password}
                        onChange={handleChange}
                        error={!!errors.password}
                        helperText={errors.password}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label={t("auth.confirmPassword")}
                        name="confirm_password"
                        type="password"
                        fullWidth
                        required
                        variant="outlined"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        error={!!errors.confirm_password}
                        helperText={errors.confirm_password}
                        sx={{ mb: 2 }}
                    />

                    <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
                        {loading ? t("auth.registering") : t("auth.register")}
                    </Button>
                </form>

                <Divider sx={{ my: 3 }}>{t("auth.or")}</Divider>

                <Button
                    onClick={handleGoogleRegister}
                    variant="outlined"
                    fullWidth
                    startIcon={<Google />}
                    sx={{ textTransform: "none" }}
                >
                    {t("auth.continueWithGoogle")}
                </Button>

                <Button
                    onClick={() => navigate("/login")}
                    sx={{ mt: 2, textTransform: "none" }}
                >
                    {t("auth.alreadyHaveAccount")}
                </Button>
            </Box>
        </Box>
    );
};

export default RegisterPage;
